import { Alert, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { isBefore } from 'date-fns';
import { formatToShortDate, convertDate } from 'utils/dates';

type AlertMessageParams = {
  estimatedShipDate: string | Date | null;
  expectedByDate: string | Date | null;
  processingDate: string | Date | null;
  kitRequestEstimatedShipDate: string | Date | null;
  kitRequestTargetShipDate: string | Date | null;
  shipPartial: boolean;
};

export const shipmentEstimateCopy = (
  kitRequestEstimatedShipDate: string | Date | null,
  estimatedShipDate: string | Date | null,
  kitRequestTargetShipDate: string | Date | null
) => {
  const formattedEstimatedDate =
    estimatedShipDate && formatToShortDate(estimatedShipDate);
  const formattedTargetDate =
    kitRequestTargetShipDate && formatToShortDate(kitRequestTargetShipDate);
  const formattedKitRequestDate =
    kitRequestEstimatedShipDate &&
    formatToShortDate(kitRequestEstimatedShipDate);

  return (
    <>
      {kitRequestTargetShipDate &&
      estimatedShipDate &&
      isBefore(
        convertDate(kitRequestTargetShipDate),
        convertDate(estimatedShipDate)
      ) ? (
        <>
          Your current scheduled date of <strong>{formattedTargetDate}</strong>{' '}
          is no longer achievable. The earliest your kit can ship is{' '}
          <strong>{formattedEstimatedDate}</strong>.
        </>
      ) : (
        <>
          Based on our estimations, initial shipments will now begin by{' '}
          <strong>{formattedEstimatedDate}</strong>. instead of{' '}
          {formattedKitRequestDate}
        </>
      )}
    </>
  );
};

export const considerShipPartialCopy = (
  processingDate: string | Date | null,
  estimatedDate: string | Date | null
) => (
  <Box component="div" sx={{ mt: 1 }}>
    Consider enabling ship partial to have part start shipping{' '}
    <strong>{processingDate && formatToShortDate(processingDate)}</strong> with
    a final shipment on{' '}
    <strong>{estimatedDate && formatToShortDate(estimatedDate)}</strong>.
  </Box>
);

export const shipCompleteCopy = (expectedByDate: string | Date | null) => (
  <em>
    Note: Since this kit is configured to ship complete, it will not ship until
    all stock is received (expected by{' '}
    {expectedByDate && formatToShortDate(expectedByDate)}).
  </em>
);

export const shipPartialCopy = (estimatedDate: string | Date | null) => (
  <em>
    Note: If you disable partial shipments, the entire kit will ship together on{' '}
    <strong>{estimatedDate && formatToShortDate(estimatedDate)}</strong>.
  </em>
);

export const finalShipmentCopy = (expectedByDate: string | Date | null) => (
  <em>
    Final shipment will be completed after receiving remaining stock (expected
    by {expectedByDate && formatToShortDate(expectedByDate)})
  </em>
);

export const targetShipDateCopy = (
  targetShipDate: string | Date | null,
  estimatedDate: string | Date | null
) => (
  <Box component="div" sx={{ mt: 1 }}>
    {!targetShipDate ? (
      <>
        You can set a scheduled ship date if you&apos;d like, or leave it empty
        to use the estimated date.
      </>
    ) : estimatedDate &&
      isBefore(convertDate(targetShipDate), convertDate(estimatedDate)) ? (
      <>Please update your scheduled ship date to acknowledge this change.</>
    ) : (
      <>
        Your scheduled ship date will still be met and does not need to be
        updated.
      </>
    )}
  </Box>
);

export const EstimatedShipDateAlert = ({
  estimatedShipDate,
  expectedByDate,
  processingDate,
  kitRequestEstimatedShipDate,
  kitRequestTargetShipDate,
  shipPartial,
}: AlertMessageParams) => (
  <Alert severity="error" sx={{ mb: 2 }}>
    <Typography variant="body1">
      {shipmentEstimateCopy(
        kitRequestEstimatedShipDate,
        estimatedShipDate,
        kitRequestTargetShipDate
      )}
      <Box component="div" sx={{ mt: 1 }}>
        {!shipPartial ? (
          <>
            <Box component="div" sx={{ mt: 1 }}>
              {shipCompleteCopy(expectedByDate)}
            </Box>
            <Box component="div" sx={{ mt: 1 }}>
              {considerShipPartialCopy(processingDate, estimatedShipDate)}
            </Box>
          </>
        ) : (
          <>
            <Box component="div" sx={{ mt: 1 }}>
              {shipPartialCopy(estimatedShipDate)}
            </Box>
            <Box component="div" sx={{ mt: 1 }}>
              {finalShipmentCopy(expectedByDate)}
            </Box>
          </>
        )}
      </Box>
    </Typography>
    <Box component="div" sx={{ mt: 1 }}>
      {targetShipDateCopy(kitRequestTargetShipDate, estimatedShipDate)}
    </Box>
  </Alert>
);
